import { ChildQuote, Quote } from '../types/quote';
import { JobQuote } from '../types/job';
import { JobStatus } from '../types/common';

/**
 * Determines if a quote should be considered active based on multiple criteria:
 * 1. Quote status should not be 'retired' or 'rejected'
 * 2. Associated job status should not be 'cancelled'
 * 3. If it's a child quote (has quote_alliance_status), the alliance should not be 'rejected' or 'retired'
 *
 * @param quote - The quote to check
 * @returns true if the quote is active, false otherwise
 */
const INACTIVE_QUOTE_STATUSES: readonly string[] = [
  'retired',
  'rejected',
  'not_accepted',
  'accepted',
];
const INACTIVE_PARENT_STATUSES: readonly string[] = [...INACTIVE_QUOTE_STATUSES, 'submitted'];

export const isQuoteActive = (quote: Quote): boolean => {
  const submittedWithChangeRequest = quote.status === 'submitted' && !!quote.change_request_message;
  if (submittedWithChangeRequest) return true;

  if (INACTIVE_QUOTE_STATUSES.includes(quote.status)) return false;
  if (
    quote.parent_quote_status != null &&
    INACTIVE_PARENT_STATUSES.includes(quote.parent_quote_status)
  )
    return false;

  if (!quote.parent_quote_id && quote.status === 'submitted' && !quote.change_request_message)
    return false;

  // Job should not be cancelled
  if (quote.job.status === 'cancelled') {
    return false;
  }

  // If this is a child quote (has quote_alliance_status), the alliance should not be rejected or retired
  if (
    quote.quote_alliance_status &&
    (quote.quote_alliance_status === 'rejected' || quote.quote_alliance_status === 'retired')
  ) {
    return false;
  }

  return true;
};

/**
 * Splits an array of quotes into active and inactive quotes
 *
 * @param quotes - Array of quotes to filter
 * @returns Object containing activeQuotes and inactiveQuotes arrays
 */
export const splitQuotesByActivity = (
  quotes: Quote[]
): { activeQuotes: Quote[]; inactiveQuotes: Quote[] } => {
  const activeQuotes: Quote[] = [];
  const inactiveQuotes: Quote[] = [];

  quotes.forEach(quote => {
    if (isQuoteActive(quote)) {
      activeQuotes.push(quote);
    } else {
      inactiveQuotes.push(quote);
    }
  });

  return { activeQuotes, inactiveQuotes };
};

/**
 * Determines the status of a quote based on multiple criteria:
 * 1. If quote.status === 'retired', returns 'retired'
 * 2. If quote.status === 'rejected', returns 'rejected'
 * 3. If quote.job.status === 'cancelled', returns 'job_cancelled'
 * 4. If quote_alliance_status === 'retired', returns 'alliance_retired'
 * 5. Otherwise returns the original quote.status
 *
 * @param quote - The quote to check
 * @returns The determined status of the quote
 */
export const getQuoteStatus = (quote: Quote): string => {
  if (quote.status === 'retired') {
    return 'retired';
  }

  if (quote.status === 'rejected' || quote.parent_quote_status === 'rejected') {
    return 'rejected';
  }

  if (quote.job.status === 'cancelled') {
    return 'job_cancelled';
  }

  if (quote.quote_alliance_status === 'retired') {
    return 'alliance_retired';
  }

  return quote.status;
};

export const canRecruiterRespondToQuote = (
  quote: ChildQuote | Quote | JobQuote,
  jobStatus: JobStatus
): boolean => {
  return (
    (jobStatus === 'open' || jobStatus === 'paused' || jobStatus === 'reviewing_quotes') &&
    quote.status === 'submitted' &&
    (!('parent_quote_id' in quote) || !quote.parent_quote_id)
  );
};
